<template>
  <div v-if="visible" class="jcm-email-overlay" @click.self="close">
    <div class="jcm-email-modal" role="dialog" aria-modal="true" aria-labelledby="jcm-email-title">
      <header class="jcm-email-modal__header">
        <div>
          <p class="jcm-email-modal__eyebrow">Current Partners · CRM</p>
          <h2 id="jcm-email-title" class="jcm-email-modal__title">Email Joint Committee</h2>
          <p class="jcm-email-modal__subtitle">{{ partnerName }}</p>
        </div>
        <button type="button" class="jcm-email-modal__close" title="Close" @click="close">
          <font-awesome-icon :icon="['fas', 'times']" />
        </button>
      </header>

      <div class="jcm-email-modal__body">
        <section class="jcm-email-section">
          <div class="jcm-email-section__head">
            <h3 class="jcm-email-section__title">Recipients</h3>
            <div class="jcm-email-section__actions">
              <button type="button" class="jcm-email-link-btn" @click="selectAll">Select all</button>
              <button type="button" class="jcm-email-link-btn" @click="clearAll">Clear</button>
            </div>
          </div>
          <p class="jcm-email-hint">All members with an email are selected by default. Uncheck anyone who should not receive this message.</p>

          <div v-if="!recipientRows.length" class="jcm-email-empty">
            No Joint Committee Members are available for this partner.
          </div>
          <ul v-else class="jcm-email-recipients">
            <li
              v-for="row in recipientRows"
              :key="row.id"
              class="jcm-email-recipient"
              :class="{ 'jcm-email-recipient--disabled': !row.hasEmail }"
            >
              <label class="jcm-email-recipient__label">
                <input
                  type="checkbox"
                  class="jcm-email-recipient__check"
                  :checked="isSelected(row.id)"
                  :disabled="!row.hasEmail || isSending"
                  @change="toggleRecipient(row.id, $event)"
                />
                <span class="jcm-email-recipient__avatar">{{ row.initials }}</span>
                <span class="jcm-email-recipient__meta">
                  <span class="jcm-email-recipient__name">{{ row.name }}</span>
                  <span class="jcm-email-recipient__email">{{ row.emailDisplay }}</span>
                </span>
                <span
                  :class="row.user_id ? 'jcm-type-badge jcm-type-badge--internal' : 'jcm-type-badge jcm-type-badge--external'"
                >
                  {{ row.user_id ? 'Internal' : 'External' }}
                </span>
              </label>
            </li>
          </ul>
          <p class="jcm-email-selected-count">{{ selectedIds.length }} selected</p>
        </section>

        <section class="jcm-email-section">
          <label class="jcm-email-field-label" for="jcm-email-subject">Subject</label>
          <input
            id="jcm-email-subject"
            v-model="subject"
            type="text"
            class="jcm-email-subject"
            :disabled="isSending"
            maxlength="500"
            placeholder="Email subject"
          />
        </section>

        <section class="jcm-email-section">
          <label class="jcm-email-field-label">Message</label>
          <div class="jcm-email-editor">
            <QuillEditor
              v-model:content="bodyHtml"
              contentType="html"
              :options="editorOptions"
              theme="snow"
            />
          </div>
        </section>
      </div>

      <footer class="jcm-email-modal__footer">
        <button type="button" class="jcm-email-btn jcm-email-btn--ghost" :disabled="isSending" @click="close">
          Cancel
        </button>
        <button
          type="button"
          class="jcm-email-btn jcm-email-btn--primary"
          :disabled="isSending || !canSend"
          @click="sendEmail"
        >
          <font-awesome-icon :icon="['fas', isSending ? 'spinner' : 'paper-plane']" :spin="isSending" />
          <span>{{ isSending ? 'Sending…' : 'Send now' }}</span>
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import gql from "graphql-tag";
import { QuillEditor } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.snow.css";

export default {
  name: "JointCommitteeEmailCompose",
  components: {
    QuillEditor,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    partnerToken: {
      type: String,
      required: true,
    },
    partnerName: {
      type: String,
      default: "",
    },
    members: {
      type: Array,
      default: function () {
        return [];
      },
    },
    senderName: {
      type: String,
      default: "",
    },
  },
  emits: ["close", "sent"],
  data: function () {
    return {
      selectedIds: [],
      subject: "",
      bodyHtml: "",
      isSending: false,
      editorOptions: {
        modules: {
          toolbar: [
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link"],
            ["clean"],
          ],
        },
        placeholder: "Write your message…",
      },
    };
  },
  computed: {
    recipientRows: function () {
      var rows = [];
      var members = this.members || [];
      for (var i = 0; i < members.length; i++) {
        var member = members[i];
        if (!member) {
          continue;
        }
        var email = this.resolveMemberEmail(member);
        var name = member.name || (member.user && member.user.name) || "Member";
        rows.push({
          id: String(member.id),
          user_id: member.user_id,
          name: name,
          email: email,
          hasEmail: !!email,
          emailDisplay: email || "No email on file",
          initials: this.getInitials(name),
        });
      }
      return rows;
    },
    canSend: function () {
      if (!this.subject || !String(this.subject).trim()) {
        return false;
      }
      if (!this.bodyHtml || this.stripHtml(this.bodyHtml).length < 3) {
        return false;
      }
      return this.selectedIds.length > 0;
    },
  },
  watch: {
    visible: function (isVisible) {
      if (isVisible) {
        this.resetCompose();
      }
    },
  },
  methods: {
    resolveMemberEmail: function (member) {
      if (member.email && String(member.email).trim()) {
        return String(member.email).trim();
      }
      if (member.user && member.user.email && String(member.user.email).trim()) {
        return String(member.user.email).trim();
      }
      return "";
    },
    getInitials: function (name) {
      if (!name) {
        return "?";
      }
      var parts = String(name).trim().split(/\s+/);
      if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
      }
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },
    stripHtml: function (html) {
      var tmp = document.createElement("div");
      tmp.innerHTML = html || "";
      return (tmp.textContent || tmp.innerText || "").trim();
    },
    buildDefaultSubject: function () {
      var name = this.partnerName || "Partnership";
      return "Joint Committee — " + name + " | Partnership Update";
    },
    buildDefaultBody: function () {
      var partner = this.partnerName || "our partnership";
      var sender = this.senderName || "Partnerships Team";
      return (
        "<p>Dear Joint Committee Members,</p>" +
        "<p>I hope this message finds you well.</p>" +
        "<p>I am writing regarding our partnership with <strong>" +
        this.escapeHtml(partner) +
        "</strong>. Please find the latest update for your attention below.</p>" +
        "<p><em>[Please edit this section with the specific update, meeting invite, or action items.]</em></p>" +
        "<p>Thank you for your continued collaboration and support.</p>" +
        "<p>Best regards,<br>" +
        this.escapeHtml(sender) +
        "<br>Partnerships Team</p>"
      );
    },
    escapeHtml: function (value) {
      return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    resetCompose: function () {
      var selected = [];
      var rows = this.recipientRows;
      for (var i = 0; i < rows.length; i++) {
        if (rows[i].hasEmail) {
          selected.push(rows[i].id);
        }
      }
      this.selectedIds = selected;
      this.subject = this.buildDefaultSubject();
      this.bodyHtml = this.buildDefaultBody();
      this.isSending = false;
    },
    isSelected: function (id) {
      return this.selectedIds.indexOf(String(id)) !== -1;
    },
    toggleRecipient: function (id, event) {
      var checked = event && event.target ? event.target.checked : false;
      var key = String(id);
      var next = [];
      for (var i = 0; i < this.selectedIds.length; i++) {
        if (this.selectedIds[i] !== key) {
          next.push(this.selectedIds[i]);
        }
      }
      if (checked) {
        next.push(key);
      }
      this.selectedIds = next;
    },
    selectAll: function () {
      var selected = [];
      var rows = this.recipientRows;
      for (var i = 0; i < rows.length; i++) {
        if (rows[i].hasEmail) {
          selected.push(rows[i].id);
        }
      }
      this.selectedIds = selected;
    },
    clearAll: function () {
      this.selectedIds = [];
    },
    close: function () {
      if (this.isSending) {
        return;
      }
      this.$emit("close");
    },
    sendEmail: async function () {
      if (!this.canSend || this.isSending) {
        return;
      }
      this.isSending = true;
      try {
        var response = await this.$apollo.mutate({
          mutation: gql`
            mutation SendCurrentPartnerJointCommitteeEmail($input: SendCurrentPartnerJointCommitteeEmailInput!) {
              sendCurrentPartnerJointCommitteeEmail(input: $input) {
                error
                error_code
                message
                queued_count
                mail_message_uuid
              }
            }
          `,
          variables: {
            input: {
              partner_token: this.partnerToken,
              member_ids: this.selectedIds,
              subject: String(this.subject).trim(),
              body_html: this.bodyHtml,
            },
          },
        });

        var payload =
          response.data && response.data.sendCurrentPartnerJointCommitteeEmail
            ? response.data.sendCurrentPartnerJointCommitteeEmail
            : null;

        if (!payload || payload.error) {
          throw new Error((payload && payload.message) || "Failed to send email.");
        }

        if (this.$swal) {
          var Toast = this.$swal.mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 2500,
          });
          Toast.fire({
            icon: "success",
            title: payload.message || "Email queued for delivery.",
          });
        }

        this.$emit("sent", payload);
        this.$emit("close");
      } catch (error) {
        var message =
          (error && error.message) ||
          (error && error.graphQLErrors && error.graphQLErrors[0] && error.graphQLErrors[0].message) ||
          "Failed to send email.";
        if (this.$swal) {
          this.$swal.fire({
            icon: "error",
            title: "Send failed",
            text: message,
          });
        }
      } finally {
        this.isSending = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.jcm-email-overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.jcm-email-modal {
  width: 100%;
  max-width: 44rem;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 1rem;
  border: 1px solid #e5e7eb;
  box-shadow: 0 24px 48px -20px rgba(15, 23, 42, 0.35);
  overflow: hidden;
}
.jcm-email-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(180deg, #f8fafc 0%, #fff 100%);
}
.jcm-email-modal__eyebrow {
  margin: 0 0 0.25rem;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #2563eb;
}
.jcm-email-modal__title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 800;
  color: #111827;
}
.jcm-email-modal__subtitle {
  margin: 0.25rem 0 0;
  font-size: 0.8125rem;
  color: #64748b;
}
.jcm-email-modal__close {
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 0.5rem;
  background: #f1f5f9;
  color: #475569;
  cursor: pointer;
}
.jcm-email-modal__close:hover {
  background: #e2e8f0;
}
.jcm-email-modal__body {
  flex: 1;
  overflow-y: auto;
  padding: 1.25rem 1.5rem;
}
.jcm-email-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}
.jcm-email-section {
  margin-bottom: 1.25rem;
}
.jcm-email-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.375rem;
}
.jcm-email-section__title {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 700;
  color: #111827;
}
.jcm-email-section__actions {
  display: flex;
  gap: 0.75rem;
}
.jcm-email-link-btn {
  border: none;
  background: transparent;
  color: #2563eb;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}
.jcm-email-hint {
  margin: 0 0 0.75rem;
  font-size: 0.75rem;
  color: #94a3b8;
}
.jcm-email-empty {
  padding: 1rem;
  border: 1px dashed #cbd5e1;
  border-radius: 0.75rem;
  color: #94a3b8;
  font-size: 0.8125rem;
  text-align: center;
}
.jcm-email-recipients {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 14rem;
  overflow-y: auto;
}
.jcm-email-recipient {
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  background: #fff;
}
.jcm-email-recipient--disabled {
  opacity: 0.55;
}
.jcm-email-recipient__label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  cursor: pointer;
}
.jcm-email-recipient__check {
  flex-shrink: 0;
}
.jcm-email-recipient__avatar {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.5rem;
  background: #eef2ff;
  color: #4338ca;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 800;
  flex-shrink: 0;
}
.jcm-email-recipient__meta {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
}
.jcm-email-recipient__name {
  font-size: 0.875rem;
  font-weight: 700;
  color: #111827;
}
.jcm-email-recipient__email {
  font-size: 0.75rem;
  color: #64748b;
  word-break: break-all;
}
.jcm-email-selected-count {
  margin: 0.5rem 0 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
}
.jcm-email-field-label {
  display: block;
  margin-bottom: 0.375rem;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #94a3b8;
}
.jcm-email-subject {
  width: 100%;
  padding: 0.625rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  outline: none;
}
.jcm-email-subject:focus {
  border-color: #3b82f6;
}
.jcm-email-editor {
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  overflow: hidden;
  background: #fff;
}
.jcm-email-editor :deep(.ql-toolbar) {
  border: none;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}
.jcm-email-editor :deep(.ql-container) {
  border: none;
  min-height: 10rem;
  font-size: 0.875rem;
}
.jcm-email-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  font-size: 0.8125rem;
  font-weight: 600;
  border-radius: 0.5rem;
  cursor: pointer;
  border: none;
}
.jcm-email-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.jcm-email-btn--ghost {
  background: #fff;
  border: 1px solid #e5e7eb;
  color: #374151;
}
.jcm-email-btn--primary {
  color: #fff;
  background: linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%);
}
.jcm-type-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  border-radius: 0.375rem;
  font-size: 0.6875rem;
  font-weight: 700;
  flex-shrink: 0;
}
.jcm-type-badge--internal {
  background: #e0e7ff;
  color: #3730a3;
}
.jcm-type-badge--external {
  background: #f1f5f9;
  color: #475569;
}
</style>
